import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { authServico } from '@/servicos/auth.servico';
import { agendarRenovacao, iniciarCoordenacaoAbas } from '@/servicos/sessao';
import { painelServico } from '@/servicos/painel.servico';
import type { Usuario, LoginInput, LoginResultado, PerfilUsuario } from '@/tipos';
import type { PapelPainel } from '@/tipos/painel.tipos';
import { atende, ehStaff, perfilEfetivo, permissoesEfetivas } from '@/utils/permissoes';

interface AuthContextData {
  usuario: Usuario | null;
  isAutenticado: boolean;
  isCarregando: boolean;
  login: (dados: LoginInput) => Promise<LoginResultado>;
  logout: () => Promise<void>;
  /** Sai de todos os aparelhos. Lança, sem sair daqui, se o servidor não confirmar. */
  logoutTodos: () => Promise<void>;
  atualizarUsuario: (usuario: Usuario) => void;
  /** Perfil efetivo (maior hierárquico) ou null. */
  perfilEfetivo: PerfilUsuario | null;
  /** Papel no painel (aluno/responsavel) para usuários sem vínculos profissionais; null para staff. */
  papel: PapelPainel;
  /** true enquanto o papel do painel ainda está sendo resolvido (evita 403 falso no refresh). */
  papelCarregando: boolean;
  /**
   * true quando o whoami falhou (rede, servidor): o papel é DESCONHECIDO, não
   * ausente. Com `papel` nulo sozinho não dá para distinguir as duas coisas.
   */
  papelErro: boolean;
  /** Refaz o whoami depois de uma falha. */
  recarregarPapel: () => void;
  temPermissao: (permissao: string) => boolean;
  temPerfil: (...perfis: PerfilUsuario[]) => boolean;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [isCarregando, setIsCarregando] = useState(true);
  const [papel, setPapel] = useState<PapelPainel>(null);
  const [papelCarregando, setPapelCarregando] = useState(true);
  const [papelErro, setPapelErro] = useState(false);
  const [tentativaPapel, setTentativaPapel] = useState(0);
  // Cada consulta ao whoami ganha um número, e só a mais recente grava. No F5
  // com sessão salva o usuário muda duas vezes quase juntas (o guardado e o do
  // /me), e a resposta mais velha, chegando por último, gravava por cima da
  // nova: papel resolvido com a tela de erro, ou o erro por cima do papel bom.
  const consultaPapel = useRef(0);

  async function resolverPapel(u: Usuario | null) {
    const minha = ++consultaPapel.current;
    const vale = () => minha === consultaPapel.current;
    if (u && !ehStaff(u)) {
      // Não é staff: pode ser aluno/responsável. Resolve via whoami.
      // Enquanto não resolve, papelCarregando evita 403 falso.
      setPapelCarregando(true);
      setPapelErro(false);
      try {
        const resolvido = await painelServico.whoami();
        if (vale()) {
          setPapel(resolvido);
          setPapelErro(false);
        }
      } catch {
        // Falha de rede ou do servidor não diz nada sobre o papel. Tratada
        // como papel nulo, mandava para "Sem permissão" quem só estava sem
        // internet; marcada como erro, o painel oferece tentar de novo.
        if (vale()) {
          setPapel(null);
          setPapelErro(true);
        }
      } finally {
        if (vale()) setPapelCarregando(false);
      }
    } else {
      setPapel(null);
      setPapelErro(false);
      setPapelCarregando(false);
    }
  }

  // Resolve o papel do painel sempre que o usuário muda (evita duplicar em
  // login + init) e a cada "tentar de novo" depois de uma falha.
  useEffect(() => {
    void resolverPapel(usuario);
  }, [usuario, tentativaPapel]);

  const recarregarPapel = useCallback(() => setTentativaPapel((n) => n + 1), []);

  // Coordenação de sessão entre abas + logout limpo vindo do interceptor/outra aba.
  useEffect(() => {
    iniciarCoordenacaoAbas();
    const aoEncerrar = () => setUsuario(null);
    window.addEventListener('sessao:encerrada', aoEncerrar);
    return () => window.removeEventListener('sessao:encerrada', aoEncerrar);
  }, []);

  // Carrega usuário do localStorage ao iniciar
  useEffect(() => {
    const usuarioLocal = authServico.getUsuarioLocal();
    if (usuarioLocal && authServico.isAutenticado()) {
      setUsuario(usuarioLocal);

      // Sessão já existente: agenda o refresh proativo (BUG-010).
      agendarRenovacao();

      // Atualiza dados do usuário
      authServico.me()
        .then(user => {
          setUsuario(user);
          localStorage.setItem('user', JSON.stringify(user));
        })
        .catch(() => {
          // Token inválido, limpa tudo
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          setUsuario(null);
        })
        .finally(() => setIsCarregando(false));
    } else {
      setIsCarregando(false);
    }
  }, []);

  const login = useCallback(async (dados: LoginInput) => {
    const resultado = await authServico.login(dados);
    // Com o código MFA pendente ainda não há sessão: o usuário só entra na
    // segunda chamada, já com o código.
    if (!resultado.mfaRequerido) setUsuario(resultado.usuario);
    return resultado;
  }, []);

  const logout = useCallback(async () => {
    await authServico.logout();
    setUsuario(null);
  }, []);

  const logoutTodos = useCallback(async () => {
    await authServico.logoutTodos();
    setUsuario(null);
  }, []);

  const atualizarUsuario = useCallback((novoUsuario: Usuario) => {
    setUsuario(novoUsuario);
    localStorage.setItem('user', JSON.stringify(novoUsuario));
  }, []);

  const efetivo: PerfilUsuario | null = (() => {
    if (!usuario) return null;
    if (usuario.perfil_efetivo) return usuario.perfil_efetivo;
    const perfis = (usuario.vinculos ?? [])
      .filter((v) => v.situacao === 'ativo')
      .map((v) => v.perfil);
    if (usuario.permissoes?.includes('admin') || usuario.permissoes?.includes('*')) {
      return 'admin';
    }
    return perfilEfetivo(perfis);
  })();

  const temPermissao = useCallback(
    (permissao: string) => {
      if (!usuario) return false;
      const disponiveis = permissoesEfetivas(efetivo, usuario.permissoes ?? []);
      return atende(disponiveis, permissao);
    },
    [usuario, efetivo]
  );

  const temPerfil = useCallback(
    (...perfis: PerfilUsuario[]) => {
      if (!usuario) return false;
      if (efetivo === 'admin') return true;
      return !!efetivo && perfis.includes(efetivo);
    },
    [usuario, efetivo]
  );

  return (
    <AuthContext.Provider
      value={{
        usuario,
        isAutenticado: !!usuario,
        isCarregando,
        login,
        logout,
        logoutTodos,
        atualizarUsuario,
        perfilEfetivo: efetivo,
        papel,
        papelCarregando,
        papelErro,
        recarregarPapel,
        temPermissao,
        temPerfil,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}
